import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, MoreHorizontal, Trash2 } from 'lucide-react';
import { api } from '@/api/client';
import { TopBar } from '@/components/TopBar';
import { Card } from '@/components/Card';
import { Spinner } from '@/components/Spinner';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { toast } from '@/state/toast';
import { useAuth } from '@/state/auth';
import type { ObjectType, Project, ProjectsResponse } from '@/types/api';

// RBAC: создавать проекты могут заказчик/куратор/админ. Проектировщик (gip) и
// эксперт работают только в назначенных проектах (бэкенд возвращает 403).
const CAN_CREATE_ROLES = ['customer', 'curator', 'admin'];

/** Empty Project State: список проектов, доступных пользователю.
    Перенесён из static/app-v2-prototype.html → ProjectsScreen. */
export default function Projects() {
  const nav = useNavigate();
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  // Вид кабинета определяет ПЕРЕКЛЮЧЁННАЯ роль (админ может смотреть глазами
  // заказчика); default_role — только запасной вариант, если переключения не было.
  const role = useAuth((s) => s.activeRole || s.user?.default_role);
  const canCreate = CAN_CREATE_ROLES.includes((role || '').toLowerCase());

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectsResponse>('/projects'),
  });

  const projects = data?.projects || [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
    );
  }, [projects, query]);

  return (
    <div className="min-h-screen bg-white">
      <TopBar notifCount={0} />

      <main className="mx-auto" style={{ maxWidth: 1280, paddingLeft: 24, paddingRight: 24, paddingTop: 48, paddingBottom: 48 }}>
        <h1 className="font-heading text-ink" style={{ fontSize: 36, fontWeight: 600, letterSpacing: '-0.02em' }}>
          Ваши проекты
        </h1>
        <p className="text-muted mt-2" style={{ fontSize: 16 }}>
          Выберите проект для продолжения работы или создайте новый
        </p>

        <div className="flex items-center justify-between mt-8" style={{ gap: 16 }}>
          <div className="relative" style={{ width: 360, maxWidth: '100%' }}>
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по шифру или названию"
              aria-label="Поиск проектов"
              className="w-full bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
              style={{ padding: '10px 14px 10px 38px', fontSize: 14 }}
            />
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center bg-white text-ink rounded-btn transition-colors hover:bg-surface"
              style={{ border: '1px solid #0A1628', padding: '10px 16px', gap: 8, fontWeight: 600, fontSize: 14 }}
            >
              <Plus size={18} />
              <span>Новый проект</span>
            </button>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center" style={{ padding: '64px 0' }}>
            <Spinner size={32} className="text-muted" />
          </div>
        )}

        {isError && (
          <div
            className="rounded-card mt-8 flex items-start"
            style={{
              padding: 24, gap: 12,
              background: 'rgba(239,68,68,.05)',
              border: '1px solid rgba(239,68,68,.2)',
            }}
          >
            <div className="flex-1">
              <h3 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600 }}>
                Не удалось загрузить проекты
              </h3>
              <p className="text-muted mt-1" style={{ fontSize: 13 }}>
                {error instanceof Error ? error.message : 'Неизвестная ошибка'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-btn text-ink bg-white border border-border hover:bg-surface"
              style={{ padding: '8px 14px', fontSize: 13, fontWeight: 500 }}
            >Повторить</button>
          </div>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <div
            className="rounded-card mt-8 text-center"
            style={{ padding: '48px 24px', background: '#F3F4F6', border: '1px dashed #E5E7EB' }}
          >
            <p className="text-muted" style={{ fontSize: 14 }}>
              {query
                ? `Ничего не найдено по запросу «${query}»`
                : canCreate
                  ? 'У вас пока нет проектов. Создайте первый.'
                  : 'Вам пока не выдан доступ ни к одному проекту.'}
            </p>
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="mt-2 text-ink hover:text-amber-hover"
                style={{ fontSize: 14, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}
              >Сбросить поиск</button>
            )}
          </div>
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <div
            className="grid mt-8"
            style={{ gap: 24, gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}
          >
            {filtered.map((p) => (
              <ProjectCard key={p.id} project={p} onOpen={() => nav('/project/' + p.id)} />
            ))}
          </div>
        )}
      </main>

      {showCreate && (
        <NewProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={(p) => { setShowCreate(false); nav('/project/' + p.id); }}
        />
      )}
    </div>
  );
}

/** Модал создания нового проекта. POST /projects {name}. */
function NewProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (p: { id: string; name: string }) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [objectType, setObjectType] = useState<ObjectType | ''>('');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Создание + (опционально) установка типа объекта вторым запросом.
  // Backend пока не принимает object_type в POST /projects, поэтому делаем
  // последовательно: создали → POST /object-type. Если второй запрос упадёт,
  // проект всё равно создан; пользователь сможет задать тип позже.
  const mutation = useMutation({
    mutationFn: async (payload: { name: string; object_type: ObjectType | null }) => {
      const created = await api.post<{ id: string; name: string }>(
        '/projects',
        { name: payload.name },
      );
      if (payload.object_type) {
        try {
          await api.post<{ ok: boolean }>(`/projects/${created.id}/object-type`, {
            object_type: payload.object_type,
          });
        } catch {
          /* не блокируем создание */
        }
      }
      return created;
    },
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      onCreated(p);
    },
    onError: (e) => {
      setErrMsg(e instanceof Error ? e.message : 'Не удалось создать проект');
    },
  });

  const trimmed = name.trim();
  const canSubmit = trimmed.length >= 1 && trimmed.length <= 500 && !mutation.isPending;

  const onSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setErrMsg(null);
    mutation.mutate({
      name: trimmed,
      object_type: objectType === '' ? null : objectType,
    });
  };

  return (
    <Modal
      title="Новый проект"
      onClose={mutation.isPending ? () => {} : onClose}
      maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>Отмена</Button>
          <Button variant="primary" onClick={() => onSubmit()} disabled={!canSubmit}>
            {mutation.isPending ? 'Создание…' : 'Создать'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <label className="block">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>
            Название проекта
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setErrMsg(null); }}
            placeholder="ШИФР-2026-001 — Реконструкция объекта…"
            autoFocus
            maxLength={500}
            disabled={mutation.isPending}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14 }}
          />
          <span className="text-muted block mt-2" style={{ fontSize: 12 }}>
            Рекомендуемый формат: «ШИФР-ГГГГ-NNN — Описание объекта». Шифр будет использоваться в карточке проекта.
          </span>
        </label>

        <label className="block mt-4">
          <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>
            Тип объекта (ПП 87)
          </span>
          <select
            value={objectType}
            onChange={(e) => setObjectType((e.target.value || '') as ObjectType | '')}
            disabled={mutation.isPending}
            className="w-full mt-2 bg-white text-ink border border-border rounded-btn focus:border-ink outline-none"
            style={{ padding: '10px 14px', fontSize: 14 }}
          >
            <option value="">Не указан — выбрать позже</option>
            <option value="areal">Площадной (капитальное строительство, Часть 2)</option>
            <option value="linear">Линейный (трубопровод, ж/д, авто, Часть 3)</option>
          </select>
          <span className="text-muted block mt-2" style={{ fontSize: 12 }}>
            От типа объекта зависит эталонный состав разделов проектной документации в проверке соответствия 87-ФЗ.
          </span>
        </label>

        {errMsg && (
          <div
            className="rounded-btn mt-4"
            style={{ padding: '10px 12px', background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', fontSize: 13, color: '#B91C1C' }}
            role="alert"
          >
            {errMsg}
          </div>
        )}

        {/* hidden submit чтобы Enter в input триггерил mutation */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
      </form>
    </Modal>
  );
}

/** Карточка проекта — переход на /project/:id при клике. */
function ProjectCard({ project, onOpen }: { project: Project; onOpen: () => void }) {
  const qc = useQueryClient();
  const statusInfo = mapStatus(project.status);
  const code = shortCode(project.name);
  const title = stripCode(project.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (ev: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const deleteMutation = useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>(`/projects/${project.id}`),
    onMutate: () => { setDeleting(true); },
    onSuccess: () => {
      toast.success(`Проект «${title}» удалён`);
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e) => {
      setDeleting(false);
      toast.error(e instanceof Error ? e.message : 'Не удалось удалить проект');
    },
  });

  const onDeleteClick = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    setMenuOpen(false);
    if (window.confirm(`Удалить проект «${project.name}» и все его данные? Действие необратимо.`)) {
      deleteMutation.mutate();
    }
  };

  return (
    <Card
      hover
      onClick={deleting ? undefined : onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' && !deleting) onOpen(); }}
      role="button"
      tabIndex={deleting ? -1 : 0}
      style={{ padding: 24, position: 'relative', opacity: deleting ? 0.4 : 1, transition: 'opacity .25s', pointerEvents: deleting ? 'none' : undefined }}
    >
      <div className="flex items-center justify-between">
        <span
          className="font-heading text-muted uppercase tracking-wider"
          style={{ fontSize: 14, fontWeight: 600 }}
        >{code}</span>
        <div className="flex items-center" style={{ gap: 8 }}>
          <span
            className="inline-flex items-center rounded-pill"
            style={{
              padding: '4px 10px',
              background: statusInfo.bg,
              color: statusInfo.color,
              fontSize: 12,
              fontWeight: 500,
            }}
          >{statusInfo.label}</span>
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label="Действия с проектом"
              className="text-muted rounded-btn"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
              onClick={(ev) => { ev.stopPropagation(); setMenuOpen((v) => !v); }}
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div
                role="menu"
                onClick={(ev) => ev.stopPropagation()}
                className="bg-white rounded-card border border-border"
                style={{ position: 'absolute', right: 0, top: 28, minWidth: 200, padding: 4, boxShadow: '0 8px 24px rgba(0,0,0,.08)', zIndex: 20 }}
              >
                <button
                  type="button"
                  onClick={onDeleteClick}
                  className="rounded-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', background: 'none', border: 'none', color: '#B91C1C', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}
                >
                  <Trash2 size={14} />
                  <span>Удалить проект</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <h3
        className="font-heading text-ink mt-3"
        style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
      >{title}</h3>
      <div className="border-t border-border mt-4 pt-4 text-xs text-muted">
        <span>ID: <span className="tabular text-ink">{project.id.slice(0, 8)}</span></span>
        {project.created_at && (
          <span className="ml-3">Создан: <span className="text-ink">{formatDate(project.created_at)}</span></span>
        )}
      </div>
    </Card>
  );
}

function mapStatus(s: Project['status']): { label: string; bg: string; color: string } {
  const k = (s || '').toLowerCase();
  if (k.includes('verif') || k.includes('verif') || k === 'verified') return { label: 'Верифицировано', bg: 'rgba(16,185,129,.1)',  color: '#10B981' };
  if (k.includes('block')  || k === 'blocked')   return { label: 'Заблокировано', bg: 'rgba(239,68,68,.1)', color: '#EF4444' };
  if (k.includes('archive')) return { label: 'Архив',          bg: '#F3F4F6',            color: '#6B7280' };
  return { label: 'На экспертизе', bg: 'rgba(245,158,11,.1)', color: '#B45309' };
}

/** Извлекает шифр из начала названия — например, «УХТ-2401 — Реконструкция…» → «УХТ-2401». */
function shortCode(name: string): string {
  const m = name.match(/^[А-ЯA-Z]{2,5}[-\s]?\d{1,6}/);
  return m ? m[0] : name.split(/[—\-]/)[0].trim().slice(0, 12);
}
function stripCode(name: string): string {
  return name.replace(/^[А-ЯA-Z]{2,5}[-\s]?\d{1,6}\s*[—\-:]\s*/, '').trim() || name;
}
function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}
